import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import { clienteTaxasApi } from '../../services/api/cliente-taxas.api';
import {
  ClienteTaxa,
  ClienteTaxasResponse,
  UpdateClienteTaxasPayload,
} from '../../types/cliente-taxa';

export interface ClienteTaxasState {
  items: ClienteTaxa[];
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  currentClienteId: string | null;
  mutating: boolean;
  mutationError: string | null;
}

const initialState: ClienteTaxasState = {
  items: [],
  status: 'idle',
  error: null,
  currentClienteId: null,
  mutating: false,
  mutationError: null,
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.response?.status === 403)
      return 'Você não tem permissão para acessar este recurso.';
    if (error.code === 'ERR_NETWORK')
      return 'Não foi possível conectar ao servidor.';
  }
  return fallback;
};

export const fetchClienteTaxasThunk = createAsyncThunk<
  ClienteTaxasResponse,
  string,
  { rejectValue: string }
>('clienteTaxas/fetch', async (clienteId, { rejectWithValue }) => {
  try {
    return await clienteTaxasApi.list(clienteId);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar taxas'));
  }
});

export const updateClienteTaxasThunk = createAsyncThunk<
  ClienteTaxasResponse,
  { clienteId: string; payload: UpdateClienteTaxasPayload },
  { rejectValue: string }
>(
  'clienteTaxas/update',
  async ({ clienteId, payload }, { rejectWithValue }) => {
    try {
      return await clienteTaxasApi.update(clienteId, payload);
    } catch (e) {
      return rejectWithValue(messageFromError(e, 'Erro ao salvar taxas'));
    }
  },
);

const clienteTaxasSlice = createSlice({
  name: 'clienteTaxas',
  initialState,
  reducers: {
    setCurrentClienteId(state, action: PayloadAction<string | null>) {
      if (state.currentClienteId !== action.payload) {
        state.items = [];
        state.status = 'idle';
        state.error = null;
        state.mutating = false;
        state.mutationError = null;
      }
      state.currentClienteId = action.payload;
    },
    clearMutationError(state) {
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClienteTaxasThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClienteTaxasThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.taxas;
      })
      .addCase(fetchClienteTaxasThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar taxas';
      })

      .addCase(updateClienteTaxasThunk.pending, (state) => {
        state.mutating = true;
        state.mutationError = null;
      })
      .addCase(updateClienteTaxasThunk.fulfilled, (state, action) => {
        state.mutating = false;
        state.items = action.payload.taxas;
      })
      .addCase(updateClienteTaxasThunk.rejected, (state, action) => {
        state.mutating = false;
        state.mutationError = action.payload ?? 'Erro ao salvar taxas';
      });
  },
});

export const { setCurrentClienteId, clearMutationError } =
  clienteTaxasSlice.actions;

export default clienteTaxasSlice.reducer;
